import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckIcon, EyeIcon, FileTextIcon, PaperclipIcon, UploadIcon, XIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@component/sheet";
import { PDFViewer } from "@component/pdf-viewer";
import {
    uploadTempCV,
    uploadTempSupportingFile,
    deleteTempSupportingFile,
} from "@service/cv";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_OTHER_FILES = 5;
const ACCEPTED_CV_EXTENSIONS = [".pdf", ".docx"];
const ACCEPTED_OTHER_EXTENSIONS = [".pdf"];

interface ResumeAndFilesUploadProps {
    onUploadComplete: () => void;
}

interface OtherFileEntry {
    id: string;
    // Local File object — only present for entries the user just dropped
    // in this session. Entries hydrated from sessionStorage on remount
    // won't have it (the browser can't persist File objects across
    // unmounts), so the UI falls back to `displayName`/`displaySize`
    // for those rows and disables local PDF preview.
    file?: File;
    displayName: string;
    displaySize?: number;
    status: "pending" | "uploading" | "success" | "error";
    errorMessage?: string;
    // Server-issued filename uuid returned by uploadTempSupportingFile.
    // Persisted in sessionStorage and sent at activation time so BE can
    // attach the file to the new user.
    serverFilename?: string;
}

interface PersistedSupportingFile {
    filename: string;
    original_name?: string;
    size_bytes?: number;
}

const SUPPORTING_FILES_STORAGE_KEY = "supporting_files_uuids";

// All supporting-file BE wiring (upload / delete / sessionStorage) is
// gated behind the demo-practice flag. Outside demo mode the component
// shouldn't be mounted at all, but the helper keeps internal calls
// inert if it ever is.
const isDemoPracticeMode = (): boolean =>
    import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";

// Persist the richer shape (filename + display metadata) so that on a
// remount we can still render the list rows even though the original
// File objects are gone. Consumers that only care about uuids (the
// signup-page activation handler) still get what they need — readers
// tolerate both legacy `string[]` and the new object form.
const persistSupportingFiles = (entries: PersistedSupportingFile[]) => {
    if (!isDemoPracticeMode()) return;
    try {
        sessionStorage.setItem(
            SUPPORTING_FILES_STORAGE_KEY,
            JSON.stringify(entries)
        );
    } catch {
        // sessionStorage can throw under privacy modes — non-fatal,
        // the FE will just lose the uuids on refresh.
    }
};

const readPersistedSupportingFiles = (): PersistedSupportingFile[] => {
    if (!isDemoPracticeMode()) return [];
    try {
        const raw = sessionStorage.getItem(SUPPORTING_FILES_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Legacy callers wrote `string[]` — coerce that into the new shape.
        return parsed
            .map((v): PersistedSupportingFile | null => {
                if (typeof v === "string") return { filename: v };
                if (v && typeof v.filename === "string") {
                    return {
                        filename: v.filename,
                        original_name: typeof v.original_name === "string" ? v.original_name : undefined,
                        size_bytes: typeof v.size_bytes === "number" ? v.size_bytes : undefined,
                    };
                }
                return null;
            })
            .filter((v): v is PersistedSupportingFile => v !== null);
    } catch {
        return [];
    }
};

const hasAcceptedExtension = (file: File, exts: string[]) => {
    const lower = file.name.toLowerCase();
    return exts.some((ext) => lower.endsWith(ext));
};

export function ResumeAndFilesUpload({ onUploadComplete }: ResumeAndFilesUploadProps) {
    const [activeTab, setActiveTab] = useState<"cv" | "other">("cv");

    // ----- CV tab state -----
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [cvError, setCvError] = useState<string>("");
    const [isUploadingCv, setIsUploadingCv] = useState<boolean>(false);
    const [isCvDragging, setIsCvDragging] = useState<boolean>(false);
    const cvInputRef = useRef<HTMLInputElement>(null);

    // ----- Other Files tab state -----
    // Hydrate from sessionStorage on first render so a re-mount of this
    // form (e.g. after a CV-parsing error sends the user back to the
    // upload step) still shows the previously-uploaded supporting files.
    const [otherFiles, setOtherFiles] = useState<OtherFileEntry[]>(() =>
        readPersistedSupportingFiles().map((entry, index) => ({
            id: `persisted-${entry.filename}-${index}`,
            displayName: entry.original_name || entry.filename,
            displaySize: entry.size_bytes,
            status: "success" as const,
            serverFilename: entry.filename,
        }))
    );
    const [otherError, setOtherError] = useState<string>("");
    const [isOtherDragging, setIsOtherDragging] = useState<boolean>(false);
    const otherInputRef = useRef<HTMLInputElement>(null);
    // PDF preview drawer — holds the entry currently being viewed and a
    // blob URL we hand to PDFViewer. The URL is created on demand and
    // revoked when the drawer closes to avoid leaking object URLs.
    const [previewEntry, setPreviewEntry] = useState<OtherFileEntry | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        // Hydrated entries (no local File) can't be previewed — the
        // preview button is disabled for those rows so this branch is
        // mostly defensive.
        if (!previewEntry?.file) {
            return;
        }
        const url = URL.createObjectURL(previewEntry.file);
        setPreviewUrl(url);
        return () => {
            URL.revokeObjectURL(url);
            setPreviewUrl(null);
        };
    }, [previewEntry]);

    // ============ CV handlers ============
    const validateCvFile = (file: File): string | null => {
        if (!hasAcceptedExtension(file, ACCEPTED_CV_EXTENSIONS)) {
            return "Only PDF and DOCX files are accepted.";
        }
        if (file.size > MAX_FILE_SIZE) {
            return "File size must be less than 5MB.";
        }
        return null;
    };

    const handleCvFile = useCallback((file: File) => {
        const error = validateCvFile(file);
        if (error) {
            setCvError(error);
            setCvFile(null);
        } else {
            setCvFile(file);
            setCvError("");
        }
    }, []);

    const handleCvDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCvDragging(false);
        if (e.dataTransfer.files.length > 0) {
            handleCvFile(e.dataTransfer.files[0]);
        }
    };

    const handleCvUpload = async () => {
        if (!cvFile) {
            setCvError("Please select a resume file.");
            return;
        }
        setIsUploadingCv(true);
        setCvError("");

        try {
            const uploadResponse = await uploadTempCV(cvFile);
            if (!uploadResponse.success) {
                throw new Error(uploadResponse.message || "Failed to upload CV.");
            }
            if (!uploadResponse.data?.filename) {
                throw new Error("Only PDF files are accepted. Please upload your resume as a PDF.");
            }
            sessionStorage.setItem("cv_uuid", uploadResponse.data.filename);
            onUploadComplete();
        } catch (err) {
            setCvError(err instanceof Error ? err.message : "An unexpected error occurred.");
        } finally {
            setIsUploadingCv(false);
        }
    };

    const removeCvFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCvFile(null);
        setCvError("");
        sessionStorage.removeItem("cv_uuid");
    };

    // ============ Other Files handlers ============
    const validateOtherFile = (file: File): string | null => {
        if (!hasAcceptedExtension(file, ACCEPTED_OTHER_EXTENSIONS)) {
            return `"${file.name}" is not a PDF.`;
        }
        if (file.size > MAX_FILE_SIZE) {
            return `"${file.name}" exceeds 5MB.`;
        }
        return null;
    };

    const addOtherFiles = useCallback((incoming: FileList | File[]) => {
        const arr = Array.from(incoming);
        const remainingSlots = MAX_OTHER_FILES - otherFiles.length;
        if (remainingSlots <= 0) {
            setOtherError(`You can upload up to ${MAX_OTHER_FILES} files only.`);
            return;
        }
        const toProcess = arr.slice(0, remainingSlots);
        const errors: string[] = [];
        const accepted: OtherFileEntry[] = [];

        toProcess.forEach((file) => {
            const err = validateOtherFile(file);
            if (err) {
                errors.push(err);
            } else {
                accepted.push({
                    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
                    file,
                    displayName: file.name,
                    displaySize: file.size,
                    status: "pending",
                });
            }
        });

        if (arr.length > remainingSlots) {
            errors.push(`Only the first ${remainingSlots} files were added (max ${MAX_OTHER_FILES}).`);
        }

        if (accepted.length > 0) {
            const next = [...otherFiles, ...accepted];
            setOtherFiles(next);
            accepted.forEach((entry) => {
                if (entry.file) startUpload(entry.id, entry.file);
            });
        }

        setOtherError(errors.join(" "));
    }, [otherFiles]);

    const startUpload = async (id: string, file: File) => {
        if (!isDemoPracticeMode()) {
            // Hard guard — supporting-file BE wiring is demo-only.
            setOtherFiles((prev) =>
                prev.map((e) =>
                    e.id === id
                        ? {
                            ...e,
                            status: "error" as const,
                            errorMessage: "Supporting file uploads are disabled.",
                        }
                        : e
                )
            );
            return;
        }
        setOtherFiles((prev) =>
            prev.map((e) => (e.id === id ? { ...e, status: "uploading" } : e))
        );
        const resp = await uploadTempSupportingFile(file);
        if (resp.success && resp.data?.filename) {
            const serverFilename = resp.data.filename;
            setOtherFiles((prev) => {
                const next = prev.map((e) =>
                    e.id === id
                        ? { ...e, status: "success" as const, serverFilename }
                        : e
                );
                persistSupportingFiles(
                    next
                        .filter((e) => Boolean(e.serverFilename))
                        .map((e) => ({
                            filename: e.serverFilename as string,
                            original_name: e.displayName,
                            size_bytes: e.displaySize,
                        }))
                );
                return next;
            });
        } else {
            setOtherFiles((prev) =>
                prev.map((e) =>
                    e.id === id
                        ? {
                            ...e,
                            status: "error" as const,
                            errorMessage: resp.message || "Upload failed",
                        }
                        : e
                )
            );
        }
    };

    const handleOtherDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOtherDragging(false);
        if (e.dataTransfer.files.length > 0) {
            addOtherFiles(e.dataTransfer.files);
        }
    };

    const removeOtherFile = (id: string) => {
        const target = otherFiles.find((e) => e.id === id);
        setOtherFiles((prev) => {
            const next = prev.filter((e) => e.id !== id);
            persistSupportingFiles(
                next
                    .filter((e) => Boolean(e.serverFilename))
                    .map((e) => ({
                        filename: e.serverFilename as string,
                        original_name: e.displayName,
                        size_bytes: e.displaySize,
                    }))
            );
            return next;
        });
        setOtherError("");
        // Fire-and-forget the BE delete for entries that successfully uploaded.
        // The endpoint is idempotent server-side so a network error here is
        // tolerable — the file will be GC'd by the 24h TTL anyway. Demo-only.
        if (target?.serverFilename && isDemoPracticeMode()) {
            void deleteTempSupportingFile(target.serverFilename);
        }
    };

    return (
        <div className="w-full">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "cv" | "other")} className="w-full">
                <TabsList className="bg-transparent h-auto p-0 space-x-0 w-full justify-start rounded-none border-b border-gray-200 dark:border-gray-700 mb-4">
                    <TabsTrigger
                        value="cv"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 text-gray-600 px-5 py-3 text-sm font-medium"
                    >
                        Resume
                    </TabsTrigger>
                    <TabsTrigger
                        value="other"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 text-gray-600 px-5 py-3 text-sm font-medium"
                    >
                        Other Files
                        {otherFiles.length > 0 && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                {otherFiles.length}
                            </span>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* ============= CV TAB ============= */}
                <TabsContent value="cv">
                    <div
                        className={`w-full h-[300px] md:h-[344px] flex justify-center items-center border-2 border-dashed rounded-lg p-6 md:p-12 text-center transition-colors ${
                            isCvDragging
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                : "border-gray-300 dark:border-gray-700 hover:border-blue-400"
                        }`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsCvDragging(true);
                        }}
                        onDragLeave={() => setIsCvDragging(false)}
                        onDrop={handleCvDrop}
                    >
                        {!cvFile ? (
                            <div className="flex flex-col">
                                <input
                                    ref={cvInputRef}
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.docx"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            handleCvFile(e.target.files[0]);
                                        }
                                    }}
                                />
                                <div className="flex justify-center">
                                    <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                                        <UploadIcon className="h-4 w-4 md:h-8 md:w-8 text-blue-500" />
                                    </div>
                                </div>
                                <h3 className="text-md md:text-xl font-medium text-gray-900 dark:text-white mb-2">
                                    Upload your resume
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 text-sm md:text-md mb-6">
                                    Drag and drop your resume file here, or click to browse.
                                </p>
                                <div className="flex flex-col items-center">
                                    <button
                                        type="button"
                                        onClick={() => cvInputRef.current?.click()}
                                        className="mb-2 px-3 md:px-6 py-1 md:py-2 text-sm md:text-md bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                                    >
                                        Browse files
                                    </button>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Supported format: PDF &amp; DOCX only (max 5MB).
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4 flex-1">
                                <div className="mx-auto w-8 md:w-12 h-8 md:h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                    <FileTextIcon className="h-4 md:h-8 w-4 md:w-8 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">{cvFile.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        onClick={removeCvFile}
                                        disabled={isUploadingCv}
                                        className="px-3 md:px-6 py-1 md:py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm flex items-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        <XIcon className="h-4 w-4 mr-1" />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {cvError && (
                        <div className="text-red-500 text-sm mt-2 flex items-center">
                            <XIcon className="h-4 w-4 mr-1" />
                            {cvError}
                        </div>
                    )}
                </TabsContent>

                {/* ============= OTHER FILES TAB ============= */}
                <TabsContent value="other">
                    <div
                        className={`w-full h-[300px] md:h-[344px] flex justify-center items-center border-2 border-dashed rounded-lg p-6 md:p-12 text-center transition-colors ${
                            isOtherDragging
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                : "border-gray-300 dark:border-gray-700 hover:border-blue-400"
                        }`}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOtherDragging(true);
                        }}
                        onDragLeave={() => setIsOtherDragging(false)}
                        onDrop={handleOtherDrop}
                    >
                        <div className="flex flex-col items-center">
                            <input
                                ref={otherInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf"
                                multiple
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        addOtherFiles(e.target.files);
                                    }
                                    // Reset so the same file can be re-selected.
                                    if (otherInputRef.current) otherInputRef.current.value = "";
                                }}
                            />
                            <div className="mb-3 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                                <PaperclipIcon className="h-6 w-6 text-blue-500" />
                            </div>
                            <h3 className="text-md md:text-lg font-medium text-gray-900 dark:text-white mb-1">
                                Upload supporting documents
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                                Drag &amp; drop PDF files here, or click to browse.
                            </p>
                            <button
                                type="button"
                                onClick={() => otherInputRef.current?.click()}
                                disabled={otherFiles.length >= MAX_OTHER_FILES}
                                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Browse files
                            </button>
                            <p className="text-xs text-gray-500 mt-2">
                                PDF only (max 5MB each, up to {MAX_OTHER_FILES} files).
                            </p>
                        </div>
                    </div>

                    {otherError && (
                        <div className="text-red-500 text-sm mt-2 flex items-start">
                            <XIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                            <span>{otherError}</span>
                        </div>
                    )}

                    {otherFiles.length > 0 && (
                        <div className="mt-4 space-y-2">
                            {otherFiles.map((entry) => (
                                <div
                                    key={entry.id}
                                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white dark:bg-gray-900"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                            <FileTextIcon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                {entry.displayName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {typeof entry.displaySize === "number"
                                                    ? `${(entry.displaySize / 1024 / 1024).toFixed(2)} MB`
                                                    : "—"}
                                                {entry.status === "uploading" && " · Uploading..."}
                                                {entry.status === "success" && " · Uploaded"}
                                                {entry.status === "error" && ` · ${entry.errorMessage || "Failed"}`}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {entry.status === "uploading" && (
                                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                        )}
                                        {entry.status === "success" && (
                                            <CheckIcon className="h-4 w-4 text-green-500" />
                                        )}
                                        {entry.file && (
                                            <button
                                                type="button"
                                                onClick={() => setPreviewEntry(entry)}
                                                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                aria-label={`View ${entry.displayName}`}
                                                title="View PDF"
                                            >
                                                <EyeIcon className="h-4 w-4 text-gray-500" />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeOtherFile(entry.id)}
                                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                            aria-label={`Remove ${entry.displayName}`}
                                        >
                                            <XIcon className="h-4 w-4 text-gray-500" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* PDF preview drawer — renders the currently-selected entry
                inside a right-side Sheet, using the existing PDFViewer
                component. */}
            <Sheet
                open={!!previewEntry}
                onOpenChange={(open) => {
                    if (!open) setPreviewEntry(null);
                }}
            >
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-[600px] md:max-w-[700px] lg:max-w-[50vw] flex flex-col p-0"
                >
                    <SheetHeader className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
                        <SheetTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2 truncate">
                            <FileTextIcon className="w-4 h-4 text-blue-600" />
                            <span className="truncate">{previewEntry?.displayName}</span>
                        </SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 min-h-0 overflow-hidden">
                        {previewUrl && <PDFViewer url={previewUrl} className="h-full rounded-none" />}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Global next-step action — sits outside the tabs so it's always
                visible regardless of which tab is active. CV is required to
                proceed; "other files" are optional supplementary uploads.
                Disabled when:
                  - no CV file is selected (`!cvFile`)
                  - CV upload is in flight (`isUploadingCv`)
                  - any "other file" upload is still pending so the user
                    doesn't submit before those finish
                Bottom padding on mobile leaves clearance for the signup
                wizard's fixed progress bar at the bottom of the viewport. */}
            <div className="mt-6 mb-24 flex justify-end">
                <button
                    type="button"
                    onClick={handleCvUpload}
                    disabled={
                        !cvFile
                        || isUploadingCv
                        || otherFiles.some((e) => e.status === "uploading")
                    }
                    className="px-6 py-2 bg-blue-600 text-white rounded-full text-sm flex items-center hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isUploadingCv ? (
                        <>
                            <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            Continue
                            <UploadIcon className="h-4 w-4 ml-2" />
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
